import {
	IAcceptRejectModeSelector,
	IGenericParameterCommitter,
	IGenericParameterExecutor,
} from "@AppBuilderLib/entities/parameter/config/shapediverStoreParameters";
import {useDefineGenericParameters} from "@AppBuilderLib/entities/parameter/model/useDefineGenericParameters";
import {useParameterStateless} from "@AppBuilderLib/entities/parameter/model/useParameterStateless";
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {useNotificationStore} from "@AppBuilderLib/features/notifications/model/useNotificationStore";
import {Logger} from "@AppBuilderLib/shared/lib/logger";
import {ISessionApi, PARAMETER_TYPE} from "@shapediver/viewer.session";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {useShallow} from "zustand/react/shallow";
import {IAppBuilder} from "../config/appbuilder";

/** Prefix used to register custom parameters */
export const CUSTOM_SESSION_ID_POSTFIX = "_appbuilder";

/** Name of input (parameter of the Grasshopper model) used to consume the custom parameter values */
const CUSTOM_DATA_INPUT_NAME = "AppBuilder";

interface Props {
	sessionApi: ISessionApi | undefined;
	appBuilderData: IAppBuilder | undefined;
	acceptRejectMode: IAcceptRejectModeSelector | boolean | undefined;
}

/**
 * This hook registers custom parameters and UI elements defined by a data output component
 * of the model named "AppBuilder". Updates of the custom parameter values are fed back to the model as JSON into
 * a text input named "AppBuilder".
 *
 * @param sessionApi
 * @param appBuilderData
 * @returns
 */
export function useAppBuilderCustomParameters(props: Props) {
	const {sessionApi, appBuilderData, acceptRejectMode} = props;
	const namespace = sessionApi?.id ?? "";
	const namespaceAppBuilder = namespace + CUSTOM_SESSION_ID_POSTFIX;

	// get the notification store
	const notifications = useNotificationStore();

	// default values and current values of the custom parameters
	const defaultCustomParameterValues = useRef<{[key: string]: any}>({});
	const customParameterValues = useRef<{[key: string]: any}>({});

	// define a callback which returns the current state of custom parameter values
	const getCustomParameterValues = useCallback(() => {
		// We want to set the value of the "AppBuilder" to a JSON string
		// of the current custom parameter values. Values for all currently
		// defined custom parameters shall be included in the JSON string.
		// Therefore we need to merge the default values with the current values.
		const customValues = {...defaultCustomParameterValues.current};
		Object.keys(customValues).forEach((id) => {
			if (id in customParameterValues.current)
				customValues[id] = customParameterValues.current[id];
		});
		// remove leftover values from custom parameters that have been removed
		Object.keys(customParameterValues.current).forEach((id) => {
			if (!(id in customValues)) delete customParameterValues.current[id];
		});

		return customValues;
	}, []);

	// "AppBuilder" parameter (used for sending values of custom parameters to the model)
	const appBuilderParam = useParameterStateless<string>(
		namespace,
		CUSTOM_DATA_INPUT_NAME,
		PARAMETER_TYPE.STRING,
	);
	const appBuilderFileParam = useParameterStateless<Blob>(
		namespace,
		CUSTOM_DATA_INPUT_NAME,
		PARAMETER_TYPE.FILE,
	);

	// prepare for adding pre-execution hook, which will set the value of the parameter named "AppBuilder"
	// to a JSON string of the current custom parameter values
	const {
		setPreExecutionHook,
		removePreExecutionHook,
		batchParameterValueUpdate,
		pendingHistoryDerivedState,
		clearPendingHistoryDerivedState,
		parameterStores,
	} = useShapeDiverStoreParameters(
		useShallow((state) => ({
			setPreExecutionHook: state.setPreExecutionHook,
			removePreExecutionHook: state.removePreExecutionHook,
			batchParameterValueUpdate: state.batchParameterValueUpdate,
			pendingHistoryDerivedState:
				state.pendingHistoryDerivedState[namespaceAppBuilder],
			clearPendingHistoryDerivedState:
				state.clearPendingHistoryDerivedState,
			parameterStores: state.parameterStores[namespaceAppBuilder],
		})),
	);

	// set the default values of the custom parameters whenever the
	// custom parameter definitions change
	useEffect(() => {
		if (appBuilderData?.parameters) {
			appBuilderData.parameters.forEach((p) => {
				// If a "value" is given as part of the custom parameter definition,
				// we use it to define the default value of the custom parameter.
				defaultCustomParameterValues.current[p.id] =
					p.value ?? p.defval;
				// If a "value" is given as part of the custom parameter definition,
				// AND if a value has already been set for the custom parameter,
				// we remove this current value of the custom parameter
				// (thereby overriding the current value with the given one).
				if (
					p.value !== undefined &&
					p.id in customParameterValues.current
				)
					delete customParameterValues.current[p.id];
			});
		}

		return () => {
			defaultCustomParameterValues.current = {};
		};
	}, [appBuilderData]);

	// executor function for changes of custom parameter values
	const executor = useCallback<IGenericParameterExecutor>(
		async (values: {[key: string]: any}, _, skipHistory) => {
			Object.keys(values).forEach(
				(key) => (customParameterValues.current[key] = values[key]),
			);

			// Note: Strictly speaking there would be no need to set the value of
			// the "AppBuilder" parameter, as it is set by the pre-execution hook anyway.

			// Note: we call actions.execute with forceImmediate `true` to
			// immediately execute the parameter change of the "AppBuilder" parameter
			// and await the execution to finish (this will call the pre-execution hook).
			// We also skip the history state update, as the pre-execution hook
			// includes the state update of custom parameters.
			// In case both the static and custom parameters are configured for
			// accept/reject mode, the changes of the custom parameters will be
			// accepted first (see IParameterChanges.priority).

			const json = JSON.stringify(getCustomParameterValues());
			if (
				appBuilderParam &&
				json.length <= appBuilderParam.definition.max!
			) {
				if (appBuilderParam.actions.setUiValue(json)) {
					if (appBuilderFileParam) {
						// clear the appBuilderFileParam in case it exists
						await batchParameterValueUpdate(
							{
								[namespace]: {
									[appBuilderParam?.definition.id]: json,
									[appBuilderFileParam.definition.id]: "",
								},
							},
							true,
						);
					} else {
						await appBuilderParam.actions.execute(
							true,
							skipHistory,
							true,
						);
					}
				} else {
					Logger.warn(
						`setUiValue failed for "${CUSTOM_DATA_INPUT_NAME}" parameter ${appBuilderParam.definition.id}, the value is not valid.`,
					);
				}
			} else if (
				appBuilderFileParam &&
				appBuilderFileParam.definition.format?.includes(
					"application/json",
				)
			) {
				if (
					appBuilderFileParam.actions.setUiValue(
						new Blob([json], {type: "application/json"}),
					)
				) {
					if (appBuilderParam) {
						// clear the appBuilderParam in case it exists
						await batchParameterValueUpdate(
							{
								[namespace]: {
									[appBuilderParam?.definition.id]: "",
									[appBuilderFileParam.definition.id]:
										appBuilderFileParam.state.uiValue,
								},
							},
							true,
						);
					} else {
						await appBuilderFileParam.actions.execute(
							true,
							skipHistory,
							true,
						);
					}
				} else {
					Logger.warn(
						`setUiValue failed for "${CUSTOM_DATA_INPUT_NAME}" parameter ${appBuilderFileParam.definition.id}, the value is not valid.`,
					);
				}
			} else if (appBuilderParam) {
				notifications.error({
					title: "Custom parameter value too long",
					message: `The custom parameter value length ${json.length} exceeds the maximum length of ${appBuilderParam.definition.max!} characters. Please use a file parameter instead.`,
				});
			} else {
				Logger.warn(
					`Could not find a suitable parameter named "${CUSTOM_DATA_INPUT_NAME}" whose type is 'String' or 'File'!`,
				);
			}
		},
		[appBuilderParam, appBuilderFileParam],
	);

	// values of custom parameters committed without a computation
	// (e.g. a parameter reset via its "resetValue" setting) must be used by the next computation
	const commit = useCallback<IGenericParameterCommitter>((id, value) => {
		customParameterValues.current[id] = value;
	}, []);

	// register the pre-execution hook
	useEffect(() => {
		if (appBuilderParam || appBuilderFileParam) {
			setPreExecutionHook(namespace, async (_values) => {
				const values = {..._values};
				const customValues = getCustomParameterValues();

				// if within the values that should be set, there is a AppBuilder parameter defined
				// we don't use the custom values, but the values within that parameter
				// as the custom values are stale in that case
				let json;
				if (appBuilderParam && values[appBuilderParam.definition.id]) {
					json = values[appBuilderParam.definition.id];
				} else {
					json = JSON.stringify(customValues);
				}

				if (
					appBuilderParam &&
					json.length <= appBuilderParam.definition.max!
				) {
					values[appBuilderParam.definition.id] = json;
				} else if (
					appBuilderFileParam &&
					appBuilderFileParam.definition.format?.includes(
						"application/json",
					)
				) {
					values[appBuilderFileParam.definition.id] = new Blob(
						[json],
						{type: "application/json"},
					);
				} else if (appBuilderParam) {
					notifications.error({
						title: "Custom parameter value too long",
						message: `The custom parameter value length ${json.length} exceeds the maximum length of ${appBuilderParam.definition.max!} characters. Please use a file parameter instead.`,
					});
				} else {
					Logger.warn(
						`Could not find a suitable parameter named "${CUSTOM_DATA_INPUT_NAME}" whose type is 'String' or 'File'!`,
					);
				}

				return {
					amendedValues: values,
					historyState: {[namespaceAppBuilder]: JSON.parse(json)},
				};
			});
		}

		return () => removePreExecutionHook(namespace);
	}, [appBuilderParam, appBuilderFileParam]);

	// create stateful definition of custom parameters
	const parameterDefinitions = useMemo(
		() =>
			(appBuilderData?.parameters ?? []).map((p) => {
				const {value, ...rest} = p;

				return {definition: rest, value};
			}),
		[appBuilderData?.parameters],
	);

	// define custom parameters and an execution callback for them
	// fall back to the session's parametersCommit setting when no explicit acceptRejectMode is provided
	const {loaded} = useDefineGenericParameters(
		namespaceAppBuilder,
		acceptRejectMode ?? sessionApi?.parametersCommit ?? false,
		parameterDefinitions,
		executor,
		namespace,
		commit,
	);

	useEffect(() => {
		if (
			!loaded ||
			!appBuilderData?.parameters ||
			!pendingHistoryDerivedState ||
			Object.keys(pendingHistoryDerivedState).length === 0
		)
			return;

		appBuilderData.parameters.forEach((p) => {
			const hasRestoredValue = Object.prototype.hasOwnProperty.call(
				pendingHistoryDerivedState,
				p.id,
			);
			if (!hasRestoredValue) return;

			defaultCustomParameterValues.current[p.id] =
				pendingHistoryDerivedState[p.id];
			if (p.id in customParameterValues.current)
				delete customParameterValues.current[p.id];

			const store = parameterStores?.[p.id];
			const {actions} = store?.getState() ?? {};
			actions?.setExecutedValue(pendingHistoryDerivedState[p.id]);
		});

		clearPendingHistoryDerivedState(namespaceAppBuilder);
	}, [
		appBuilderData,
		clearPendingHistoryDerivedState,
		loaded,
		namespaceAppBuilder,
		parameterStores,
		pendingHistoryDerivedState,
	]);

	// Don't block parameters history initialization if there are no custom parameters
	if (parameterDefinitions.length === 0) {
		return {loaded: true};
	}

	return {loaded};
}
